<template>
  <div class="contain">
    <!-- 검색 영역 -->
    <div class="search_form" ref="search_form">
      <el-form
        :inline="true"
        :model="searchForm"
        :label-position="`left`"
        label-suffix=":"
        :size="size"
        @submit.native.prevent>
        <el-row>
          <!-- 고른 매장 원천(사업장 관리자·관리점·운영사)에만 있는 칸 — 세션 매장 원천은 매장을 고르지 않는다 -->
          <el-col v-if="source.picksStore" :md="8">
            <el-form-item :label="$t('매장')">
              <StorePicker v-model="storeCd" :options="store" @change="storeInputSelect" />
            </el-form-item>
          </el-col>
          <el-col :md="8">
            <el-form-item :label="$t('할인명')">
              <el-input v-model="searchForm.discountNm" @keyup.enter.native="search" clearable></el-input>
            </el-form-item>
          </el-col>
          <el-col :md="8">
            <el-form-item :label="$t('사용 가능 여부')">
              <combo
                :data="mainVue.code.useYn"
                :label="$t('전체')"
                :model="searchForm.useYn"
                @change="searchForm.useYn = $event"></combo>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </div>

    <!-- 버튼 -->
    <div class="buttons" ref="buttons">
      <el-button
        v-if="checkAuthActionAccess(source.actions.search)"
        :size="size"
        @click="search"
        icon="el-icon-search"
        :disabled="!can.search"
        >{{ $t('검색') }}</el-button
      >
      <el-button
        v-if="checkAuthActionAccess(source.actions.add)"
        :size="size"
        @click="add"
        icon="el-icon-circle-plus-outline"
        :disabled="!can.add"
        >{{ $t('등록') }}</el-button
      >
      <el-button
        v-if="checkAuthActionAccess(source.actions.up)"
        :size="size"
        @click="up"
        icon="el-icon-edit-outline"
        :disabled="!can.up"
        >{{ $t('수정') }}</el-button
      >
      <el-button
        v-if="checkAuthActionAccess(source.actions.del)"
        :size="size"
        @click="del"
        icon="el-icon-remove-outline"
        :disabled="!can.del"
        >{{ $t('삭제') }}</el-button
      >
    </div>

    <!-- 리스트 -->
    <el-table
      :height="tableHeight"
      ref="table"
      border
      stripe
      highlight-current-row
      tooltip-effect="dark"
      :data="tableData"
      :size="size"
      :empty-text="$t('데이타 없음')"
      @row-click="handleRowClick"
      @select="handleRowChecked"
      @select-all="handleRowChecked">
      <el-table-column
        :label="tableData.length.toString()"
        type="index"
        width="55"
        align="center"
        sortable
        label-class-name="data_cnt"></el-table-column>
      <el-table-column type="selection" width="45" align="center"></el-table-column>
      <el-table-column :label="$t('할인명')" prop="discountNmLan" min-width="200" sortable></el-table-column>
      <el-table-column
        :label="$t('할인 대상 구분')"
        prop="discountTarget.nmLan"
        min-width="130"
        align="center"
        sortable></el-table-column>
      <el-table-column
        :label="$t('판매 방식')"
        prop="salesTpNms"
        min-width="150"
        align="center"
        show-overflow-tooltip></el-table-column>
      <el-table-column
        :label="$t('단말 구분')"
        prop="discountClientsNm"
        min-width="130"
        align="center"
        sortable></el-table-column>
      <el-table-column
        :label="$t('할인 유형')"
        prop="discountTp.nmLan"
        min-width="130"
        align="center"
        sortable></el-table-column>
      <el-table-column :label="$t('할인 금액/율')" prop="tmp" min-width="130" align="right" sortable></el-table-column>
      <el-table-column :label="$t('표시 순서')" prop="ord" min-width="100" align="center" sortable></el-table-column>
      <el-table-column
        :label="$t('사용 가능 여부')"
        prop="useYn"
        min-width="110"
        align="center"
        :formatter="mainVue.formatterCode"></el-table-column>
      <!-- 쿠폰 발급 상태·누계(R-B) — 서버가 파생해 목록에 싣는다(offerStatus·issuedCnt·usedDiscountAmt), 화면은 그리기만 -->
      <el-table-column :label="offerText.statusColumn" min-width="100" align="center">
        <template slot-scope="scope">
          <el-tag
            v-if="offerStatusBadge(scope.row.offerStatus)"
            size="mini"
            :type="offerStatusBadge(scope.row.offerStatus).type"
            >{{ offerStatusBadge(scope.row.offerStatus).label }}</el-tag
          >
        </template>
      </el-table-column>
      <el-table-column
        :label="offerText.issuedColumn"
        prop="issuedCnt"
        min-width="130"
        align="right"
        sortable
        :formatter="issuedSummary"></el-table-column>
      <el-table-column
        :label="offerText.usedColumn"
        prop="usedDiscountAmt"
        min-width="190"
        align="right"
        sortable
        :formatter="usedSummary"></el-table-column>
      <el-table-column :label="$t('설명')" prop="discountDesc" min-width="300" show-overflow-tooltip></el-table-column>
    </el-table>

    <!-- 입력 팝업 -->
    <el-dialog :title="inputForm.title" :visible.sync="dialogFormVisible" fullscreen>
      <el-form
        :model="inputForm"
        label-width="140px"
        :label-position="$device.mobile ? 'top' : 'left'"
        label-suffix=":"
        :size="size"
        @submit.native.prevent>
        <el-row :gutter="20">
          <el-col :md="inputForm.discountInfos.length > 0 ? 12 : 24">
            <el-tabs value="info">
              <el-tab-pane :label="$t('기본 정보')" name="info">
                <el-row :gutter="20">
                  <el-col :span="inputForm.discountInfos.length > 0 ? 24 : 12">
                    <el-form-item :label="$t('할인명')" :error="formError.discountNm" required>
                      <el-input v-model="inputForm.discountNm" maxlength="50" clearable show-word-limit>
                        <el-button
                          v-if="lans.length > 1"
                          slot="append"
                          :size="size"
                          @click="dictionaryDialogFormVisible = true"
                          icon="el-icon-search"
                          >{{ $t('다국어 조회') }}</el-button
                        >
                      </el-input>

                      <!-- 사전 선택 -->
                      <DictionaryDialog
                        :visible="dictionaryDialogFormVisible"
                        @close="dictionaryDialogFormVisible = false"
                        @selectDictionary="selectDictionary" />
                    </el-form-item>
                    <el-form-item :label="$t('할인 대상')" :error="formError['discountTarget.cd']" required>
                      <code-combo
                        :data="discountTarget"
                        :model="inputForm.discountTarget"
                        @change="inputForm.discountTarget = $event"></code-combo>
                    </el-form-item>
                    <el-form-item :label="$t('판매 방식')" :error="formError.salesTps" required>
                      <mcode-combo
                        :data="salesTp"
                        :model="inputForm.salesTps"
                        @change="inputForm.salesTps = $event"></mcode-combo>
                    </el-form-item>
                    <el-form-item :label="$t('단말 구분')" :error="formError.discountClients" required>
                      <mcode-combo
                        :data="discountClient"
                        :model="inputForm.discountClients"
                        @change="inputForm.discountClients = $event"></mcode-combo>
                    </el-form-item>
                    <el-form-item :label="$t('할인 구분')" :error="formError['discountTp.cd']" required>
                      <code-combo
                        :data="discountTp"
                        :model="inputForm.discountTp"
                        @change="inputForm.discountTp = $event"></code-combo>
                    </el-form-item>
                    <el-form-item
                      :label="$t('할인 금액')"
                      :error="formError.price"
                      required
                      v-show="inputForm.discountTp.val == 'price'">
                      <el-input-number
                        v-model="inputForm.price"
                        :step="100"
                        :min="0"
                        :max="90000000"
                        style="width: 200px"></el-input-number>
                    </el-form-item>
                    <el-form-item
                      :label="$t('할인율')"
                      :error="formError.percente"
                      required
                      v-show="inputForm.discountTp.val == 'percent'">
                      <el-input-number
                        v-model="inputForm.percente"
                        :min="0"
                        :max="100"
                        style="width: 200px"></el-input-number>
                    </el-form-item>
                    <el-form-item :label="$t('표시 순서')" :error="formError.ord" required>
                      <el-input-number v-model="inputForm.ord" :min="1" :max="99"></el-input-number>
                    </el-form-item>

                    <el-form-item :label="$t('쿠폰 사용 가능 여부')" required>
                      <combo
                        :data="mainVue.code.useYn"
                        :model="inputForm.couponApplicableYn"
                        @change="inputForm.couponApplicableYn = $event"></combo>
                    </el-form-item>

                    <el-form-item v-if="inputForm.couponApplicableYn == 'Y'">
                      <el-checkbox v-model="inputForm.couponOnlyYn" true-label="Y" false-label="N" :size="size">
                        {{ $t('쿠폰 전용 (쿠폰 사용 시에만 할인 적용)') }}
                      </el-checkbox>
                    </el-form-item>

                    <!--
                      쿠폰 전용 할인에만 보이는 칸 — 쿠폰을 낼 수 있는 할인은 쿠폰 전용뿐이다(KT R-T 결정 ①, 판정은
                      isCouponOnlyDiscount 한 곳).
                      · 파트너 앱(콜팝) 「쿠폰 받기」 정책 — 유효기간·재발급은 매장이 할인에 정한다(파트너 API 설계 결정 D1·D2,
                        R-T 리뷰 F4). 숨어도 값은 폼에 남아 그대로 저장된다(이름·기본값 그대로).
                      · 발급 한도·게시 기간·콜팝 랜덤팝업(R-B, D4·D9) — 숨으면 저장 본문이 이 칸들을 비운다(discountSavePayload).
                        빈 숫자 칸 = 무제한이며 폼에서는 undefined 다(discountOfferForm).
                    -->
                    <template v-if="couponOnly">
                      <el-form-item :label="$t('쿠폰 유효기간(일)')" :error="formError.couponValidityDays">
                        <el-input-number
                          v-model="inputForm.couponValidityDays"
                          :min="1"
                          :max="365"
                          :size="size"></el-input-number>
                        <span class="ml-2">{{ $t('파트너 앱에서 받은 쿠폰이 유효한 기간 (발급일 포함)') }}</span>
                      </el-form-item>
                      <el-form-item>
                        <el-checkbox v-model="inputForm.couponReissueYn" true-label="Y" false-label="N" :size="size">
                          {{ $t('사용·만료 뒤 같은 회원에게 다시 발급 허용 (파트너 앱)') }}
                        </el-checkbox>
                      </el-form-item>
                      <el-form-item :label="offerText.issueLimitLabel">
                        <el-input-number
                          v-model="inputForm.issueLimitCnt"
                          :min="1"
                          :max="99999999"
                          :precision="0"
                          :placeholder="offerText.unlimited"
                          :size="size"></el-input-number>
                        <el-tooltip :content="offerText.limitHelp" placement="top">
                          <i class="el-icon-question offer-help"></i>
                        </el-tooltip>
                      </el-form-item>
                      <el-form-item :label="offerText.budgetLimitLabel">
                        <el-input-number
                          v-model="inputForm.budgetLimitAmt"
                          :min="1"
                          :max="9999999999"
                          :step="1000"
                          :precision="0"
                          :placeholder="offerText.unlimited"
                          :size="size"></el-input-number>
                        <el-tooltip :content="offerText.limitHelp" placement="top">
                          <i class="el-icon-question offer-help"></i>
                        </el-tooltip>
                      </el-form-item>
                      <el-form-item :label="offerText.offerPeriodLabel">
                        <!-- 끝 시각은 포함이다 — 날짜만 고르면 그날 23:59:59 까지 -->
                        <el-date-picker
                          v-model="inputForm.offerStartDt"
                          type="datetime"
                          value-format="yyyy-MM-dd HH:mm:ss"
                          default-time="00:00:00"
                          :placeholder="offerText.offerStartPlaceholder"
                          :size="size"></el-date-picker>
                        <span class="offer-period-sep">~</span>
                        <el-date-picker
                          v-model="inputForm.offerEndDt"
                          type="datetime"
                          value-format="yyyy-MM-dd HH:mm:ss"
                          default-time="23:59:59"
                          :placeholder="offerText.offerEndPlaceholder"
                          :size="size"></el-date-picker>
                        <el-tooltip :content="offerText.limitHelp" placement="top">
                          <i class="el-icon-question offer-help"></i>
                        </el-tooltip>
                      </el-form-item>
                      <el-form-item>
                        <el-checkbox v-model="inputForm.popupRequestedYn" true-label="Y" false-label="N" :size="size">
                          {{ offerText.popupLabel }}
                        </el-checkbox>
                        <el-tooltip :content="offerText.popupHelp" placement="top">
                          <i class="el-icon-question offer-help"></i>
                        </el-tooltip>
                      </el-form-item>
                    </template>

                    <el-form-item :label="$t('사용 가능 여부')" required>
                      <combo
                        :data="mainVue.code.useYn"
                        :model="inputForm.useYn"
                        @change="inputForm.useYn = $event"></combo>
                    </el-form-item>

                    <el-form-item :label="$t('설명')" :error="formError.discountDesc">
                      <el-input
                        v-model="inputForm.discountDesc"
                        type="textarea"
                        maxlength="250"
                        show-word-limit></el-input>
                    </el-form-item>
                  </el-col>
                </el-row>
              </el-tab-pane>
            </el-tabs>
          </el-col>
          <el-col :md="12" v-if="inputForm.discountInfos.length > 0">
            <el-tabs v-model="activiTab">
              <el-tab-pane
                v-for="(item, index) in inputForm.discountInfos"
                :key="item.lanTp.id"
                :label="item.lanTp.nm"
                :name="item.lanTp.id">
                <el-form-item
                  :label="$t('할인명')"
                  :error="formError['discountInfos[' + index + '].discountInfoNm']"
                  required>
                  <el-input
                    v-model="inputForm.discountInfos[index].discountInfoNm"
                    maxlength="50"
                    clearable
                    show-word-limit></el-input>
                </el-form-item>

                <el-form-item
                  :label="$t('할인 금액')"
                  :error="formError['discountInfos[' + index + '].price']"
                  required
                  v-show="inputForm.discountTp.val == 'price'"
                  v-if="internationalPayYn">
                  <el-input-number
                    v-model="inputForm.discountInfos[index].price"
                    :min="0"
                    :max="90000000"
                    style="width: 200px"></el-input-number>
                </el-form-item>

                <el-form-item
                  :label="$t('할인율')"
                  :error="formError['discountInfos[' + index + '].percente']"
                  required
                  v-show="inputForm.discountTp.val == 'percent'"
                  v-if="internationalPayYn">
                  <el-input-number
                    v-model="inputForm.discountInfos[index].percente"
                    :min="0"
                    :max="100"
                    style="width: 200px"></el-input-number>
                </el-form-item>
              </el-tab-pane>
            </el-tabs>
          </el-col>
        </el-row>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button icon="el-icon-check" :size="size" @click="save" :disabled="!can.save">{{ $t('저장') }}</el-button>
        <el-button icon="el-icon-close" :size="size" @click="closeDialogVisible = true">{{ $t('닫기') }}</el-button>
        <el-dialog
          title="Warning"
          :visible.sync="closeDialogVisible"
          width="300px"
          :modal="false"
          :modal-append-to-body="false"
          :append-to-body="true"
          center>
          <span>정말로 페이지를 닫으시겠습니까?</span>
          <span slot="footer" class="dialog-footer">
            <el-button @click="closeDialogVisible = false">아니요</el-button>
            <el-button
              type="primary"
              @click="
                closeDialogVisible = false
                dialogFormVisible = false
              "
              >예</el-button
            >
          </span>
        </el-dialog>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import DictionaryDialog from '@/components/dictionary/DictionaryDialog.vue'
import StorePicker from '@/components/store/StorePicker.vue'
import mainVueMixin from '@/mixins/mainVueMixin'
import storeOptionResolver from '@/mixins/storeOptionResolver'
import { util } from '@/mixins/utilMixin'
import { initData } from '@/utils/constants'
import {
  DISCOUNT_OFFER_TEXT,
  discountOfferForm,
  discountSavePayload,
  isCouponOnlyDiscount,
  issuedSummary,
  offerStatusBadge,
  usedSummary,
} from '@/utils/discountOffer'
import {
  DISCOUNT_STORE_SOURCE,
  discountActions,
  discountAddBody,
  discountListQuery,
  discountStoreContext,
  discountStoreSource,
} from '@/utils/discountStoreSource'
import { latestGuard } from '@/utils/latestGuard'
import { parseStoreCd } from '@/utils/posDeliverySoldout'
import { STORE_OPTIONS_API, storeOptionsOf, storeOptionsQuery } from '@/utils/storeOptions'
import { syncLangValue } from '@/utils/syncLangValue'

/**
 * **할인 관리 — 두 라우트가 쓰는 한 화면.** 매장을 어디서 얻는가([storeSource])만 다르다:
 *
 * - 세션 원천(매장 계정, 기본) — `/discount/store`, 언어 축은 `GET /store/getStore`.
 * - 고른 원천(사업장 관리자·관리점·운영사) — 매장 칸(`StorePicker`, 후보 `GET /store/combo`)에서 고르고
 *   `/discount?storeCd=`·`POST /discount`(본문 `store`)·`/discount/{cd}` 를 쓴다. 언어 축은 고른 매장의 후보 행이
 *   준다([storeOptionResolver] — 후보에 없으면 상세를 읽는다). `?storeCd=` 로 들어오면 그 매장으로 연다.
 *
 * 원천별 경로·액션·질의·등록 본문의 차이는 `utils/discountStoreSource` 한 곳이다. 두 라우트 사이의 클라이언트
 * 이동은 **새 인스턴스**다 — `App.vue` 의 `<router-view>` 가 라우트 레코드를 키로 쓴다(`utils/routeLayout` 의
 * `routeViewKey`). 원천은 인스턴스 수명 동안 바뀌지 않으므로, 원천별 초기화는 `data()`·`created()` 가 맡는다.
 *
 * ## 매장 컨텍스트는 **확정된 한 값**이다 — `storeCd`(고르는 중)와 `storeContext`(확정)는 다르다
 *
 * `storeCd` 는 선택기의 입력값일 뿐이다 — 사람이 고른 순간 바뀌지만 그 매장의 언어 축은 아직 없다.
 * **확정 컨텍스트**(`storeContext`, `utils/discountStoreSource` 의 `discountStoreContext`)는 대상 매장과 언어 축·
 * 국제결제를 한 값으로 들고, 메타데이터가 도착한 순간 **한 번에** 선다([confirmStoreContext]). 목록 질의·등록 대상·
 * 폼의 언어 행은 전부 이 값에서 읽고, 검색·등록·수정·삭제·저장의 가능 여부도 이 값에서 파생한다([can],
 * `discountActions`) — 버튼마다 잠금 플래그를 두고 자리마다 푸는 방식은 쓰지 않는다(한 자리가 빠지면 다른 매장의
 * 설정으로 저장된다 — R-G 2차 교차 리뷰).
 *
 * - **확정** — 세션 원천은 진입 때 세션 매장(`GET /store/getStore`)으로, 고른 원천은 고른 매장(사람의 선택·주소의
 *   `?storeCd=`)의 셀렉트 행으로.
 * - **비움** — 매장 전환이 시작되는 자리([applyStoreOption], 사람의 선택·쿼리 씨앗) 하나. [leaveStoreContext] 가
 *   컨텍스트를 비우고 나가 있는 목록·수정 상세(회차 [listGuard]·[detailGuard])·표·선택·열린 폼을 버린다. **열린 폼은
 *   확인 없이 닫는다** — 앞 매장의 언어 축으로 선 폼은 새 매장에 저장할 수 없다.
 * - **복구** — 고른 매장의 메타데이터를 못 읽으면 [storeOptionResolver] 의 되돌리기 계약대로 마지막 확정 매장의 행으로
 *   돌아가 컨텍스트를 다시 세운다([revertStoreSelection]). 주소의 매장이 진입 때 실패하면 확정 매장이 아직 없으므로
 *   선택기를 비운다 — 사람이 고르면 선다.
 */
export default {
  mixins: [mainVueMixin, storeOptionResolver],

  components: {
    DictionaryDialog,
    StorePicker,
  },

  props: {
    /** [DISCOUNT_STORE_SOURCE] — 라우트가 정한다 */
    storeSource: { type: String, default: DISCOUNT_STORE_SOURCE.SESSION },
  },

  data: function () {
    // 경로(`url`)는 원천에서, 버튼 가능 여부(`btn`)는 확정 컨텍스트에서 파생한다(computed) — 표준 상태 템플릿의
    // 두 칸은 들지 않는다
    const { url, btn, ...data } = initData()
    /** 고른 원천의 매장 후보(`GET /store/combo`) — [storeOptionResolver] 가 같은 이름으로 읽는다 */
    data.store = []
    /**
     * 고른 원천의 **선택기 입력값** — 쿼리 `storeCd` 로 들어오면 그 매장에서 시작한다(세션 원천은 쓰지 않는다).
     * 확정이 아니다: 목록·등록의 대상은 [storeContext] 가 정한다
     */
    data.storeCd = parseStoreCd(this.$route.query.storeCd)
    /** 확정 매장 컨텍스트(`discountStoreContext`) — 없으면 `null`(진입 직후·전환 중) */
    data.storeContext = null
    data.discountTarget = []
    data.salesTp = []
    data.discountClient = []
    data.discountTp = []
    data.activiTab = ''

    data.searchForm = this.initSearchForm()
    data.inputForm = this.initInputForm()
    data.formError = this.initFormError()

    data.popTableData = []

    data.dictionaryDialogFormVisible = false
    data.searchText = ''

    data.closeDialogVisible = false

    data.offerText = DISCOUNT_OFFER_TEXT

    /** 목록 조회의 회차 — 늦게 온 앞 회차(앞 매장)의 목록이 최신을 덮지 않게 */
    data.listGuard = latestGuard()
    /** 수정 상세의 회차 — 매장이 바뀐 뒤 도착한 앞 매장의 상세가 폼을 열지 않게 */
    data.detailGuard = latestGuard()
    return data
  },
  computed: {
    /** 매장 원천 — 경로·액션·질의·등록 본문의 차이([discountStoreSource]) */
    source: function () {
      return discountStoreSource(this.storeSource)
    },
    /** 이 원천의 CRUD 경로 — 원천에서 파생한다(따로 들지 않는다) */
    url: function () {
      return this.source.url
    },
    /** 검색·등록·수정·삭제·저장을 할 수 있는가 — 확정 컨텍스트 하나에서 나온다(`discountActions`) */
    can: function () {
      return discountActions(this.storeContext, this.selectData, this.checkedData)
    },
    // 폼의 언어 행·사전 조회 순서·국제결제 — 전부 확정 컨텍스트의 것이다(없으면 비어 있고, 그때는 폼을 열 수 없다)
    storeLans: function () {
      return this.storeContext ? this.storeContext.storeLans : []
    },
    lans: function () {
      return this.storeContext ? this.storeContext.lans : []
    },
    internationalPayYn: function () {
      return !!(this.storeContext && this.storeContext.internationalPayYn)
    },
    // 발급 한도·게시 기간·랜덤팝업 칸을 보이는가(D4) — 판정은 utils/discountOffer 한 곳
    couponOnly: function () {
      return isCouponOnlyDiscount(this.inputForm)
    },
  },
  created: function () {
    this.initDiscountTarget()
    this.initSalesTp()
    this.initDiscountClient()
    this.initDiscountTp()

    // 목록은 컨텍스트가 확정된 뒤에 읽는다 — 세션 원천은 세션 매장이 오면, 고른 원천은 매장 칸의 `change`
    // (첫 매장 기본값 포함)나 쿼리 매장이 확정하면
    if (this.source.picksStore) {
      this.initStoreOptions()
    } else {
      this.initStore()
    }
  },
  // 떠나는 화면의 나가 있는 목록·수정 상세는 버린다 — 원천이 다른 라우트로 옮기면 새 인스턴스다(`App.vue` 의 키).
  // 무효화가 아니라 **닫는다**: 떠난 뒤 도착한 세션 매장·매장 후보(가드 없는 legacy 콜백)가 목록 조회를 새로 시작해도
  // 그 회차는 최신이 아니다(`utils/latestGuard` 의 close)
  beforeDestroy: function () {
    this.listGuard.close()
    this.detailGuard.close()
  },
  mounted: function () {
    setTimeout(
      function () {
        this.$nextTick(
          function () {
            this.mainVue.currVue = this
          }.bind(this)
        )
      }.bind(this),
      500
    )
  },
  watch: {
    formError: {
      deep: true,
      handler: function (newVal, oldVal) {
        // 벨리데이션 메세지가 값은 있는데 Vue에서 표시 안해주는 현상 처리 하기 위함
        setTimeout(
          function () {
            var tmp = this.inputForm.discountNm
            this.inputForm.discountNm = this.inputForm.discountNm + ' '
            this.inputForm.discountNm = tmp

            var i = 100
            for (let val in newVal) {
              if (val.indexOf('discountInfos') > -1) {
                if ('' != newVal[val]) {
                  if (i > val.replace(/[^0-9]*/g, '')) {
                    i = parseInt(val.replace(/[^0-9]*/g, ''))
                  }
                }
              }
            }

            if (100 != i) {
              this.activiTab = this.inputForm.discountInfos[i].lanTp.id
            }
          }.bind(this),
          500
        )
      },
    },
    // 단가 수정 이벤트 감지
    'inputForm.price': function (newVal) {
      // 타국화페 결제 사용안할때
      if (!this.internationalPayYn) {
        syncLangValue(this.inputForm.discountInfos, 'price', this.inputForm.price)
      }
    },
    // 할인율 수정 이벤트 감지
    'inputForm.percente': function (newVal) {
      // 타국화페 결제 사용안할때
      if (!this.internationalPayYn) {
        syncLangValue(this.inputForm.discountInfos, 'percente', this.inputForm.percente)
      }
    },
  },
  methods: {
    // 목록의 쿠폰 상태 배지·발급 / 한도·사용 / 한도 — 문구는 utils/discountOffer 한 곳
    offerStatusBadge: offerStatusBadge,
    issuedSummary: issuedSummary,
    usedSummary: usedSummary,
    // 검색 조건 초기화
    initSearchForm: function () {
      return {
        discountNm: '',
        useYn: 'Y',
      }
    },
    // 입력폼 초기화
    initInputForm: function () {
      return {
        statu: 'I',
        title: '',
        discountCd: 0,
        discountNm: '',
        price: 0,
        percente: 0,
        discountInfos: [],
        discountTarget: { cd: null, nm: '' },
        salesTps: [],
        discountClients: [],
        discountTp: { cd: null, nm: '' },
        ord: 0,
        useYn: 'Y',
        couponApplicableYn: 'Y',
        couponOnlyYn: 'N',
        couponValidityDays: 30,
        couponReissueYn: 'N',
        // 발급 한도·게시 기간·랜덤팝업 — 기본은 무제한·제한 없음·신청 안 함
        ...discountOfferForm(),
        discountDesc: '',
      }
    },
    // 오류 메세지 초기화
    initFormError: function () {
      return {
        discountNm: '',
        price: '',
        percente: '',
        'discountTarget.cd': '',
        salesTps: '',
        'discountTp.cd': '',
        discountClients: '',
        ord: '',
        useYn: '',
        couponApplicableYn: '',
        couponOnlyYn: '',
        couponValidityDays: '',
        discountDesc: '',
      }
    },
    // 그리드 선택 이벤트
    handleRowClick: function (val) {
      this.selectData = val
    },
    // 체크 박스 체크 이벤트
    handleRowChecked: function (selection, row) {
      this.checkedData = selection.map((item) => item.discountCd)
    },
    // 세션 원천 — 진입 때 세션 매장으로 컨텍스트를 확정한다
    initStore: function () {
      this.mainVue.$axios.search(
        '/store/getStore',
        null,
        function (result) {
          this.confirmStoreContext(discountStoreContext(this.source, result))
        }.bind(this)
      )
    },
    // 고른 원천 — 매장 후보. 기본 매장은 [StorePicker] 가 세우고 그 `change` 가 첫 조회를 부른다.
    // 쿼리 매장으로 들어왔으면 칸이 값을 이미 가져 기본값을 세우지 않으므로 여기서 그 매장을 적용한다 — 주소가 화면
    // 전체의 매장을 정한 갈래라, 못 읽으면 선택기를 되돌리고 주소의 매장을 열 수 없다고 말한다([storeRouteSelect])
    initStoreOptions: function () {
      this.mainVue.$axios.search(
        STORE_OPTIONS_API,
        storeOptionsQuery(),
        function (result) {
          this.store = storeOptionsOf(result)

          if (this.storeCd != null) this.storeRouteSelect(this.storeCd)
        }.bind(this)
      )
    },
    // 고른 원천의 매장 전환 — 진입점 둘(사람·씨앗)은 [storeOptionResolver] 가 갖는다. 전환이 시작되는 자리가
    // 여기 하나라 확정 컨텍스트도 여기서 비우고(메타데이터를 기다리기 전에), 메타데이터가 오면 한 번에 확정한다
    applyStoreOption: async function (storeCd, pending) {
      this.leaveStoreContext()

      const option = await pending
      if (!option) return

      this.confirmStoreContext(discountStoreContext(this.source, option))
    },
    // 고른 매장의 한 줄을 못 읽었다 — [storeOptionResolver] 의 되돌리기 계약을 이 화면의 선택 모델로 답한다.
    // 선택기와 컨텍스트를 **마지막 확정 매장의 행**으로 함께 되돌린다. 확정 매장이 아직 없으면 비운 채다
    revertStoreSelection: function (confirmedStoreOption) {
      this.storeCd = confirmedStoreOption ? confirmedStoreOption.storeCd : null
      if (confirmedStoreOption) this.confirmStoreContext(discountStoreContext(this.source, confirmedStoreOption))
    },
    // 컨텍스트를 확정하고 그 매장의 목록을 읽는다 — 대상 매장과 언어 축이 한 값으로 함께 선다
    confirmStoreContext: function (context) {
      this.storeContext = context
      this.search()
    },
    // 전환이 시작됐다 — 확정 컨텍스트를 비우고 앞 매장의 것(나가 있는 목록·수정 상세, 표와 선택, 열린 폼)을 버린다.
    // 할 수 있는 일은 컨텍스트에서 파생하므로([can]) 따로 잠글 것이 없다
    leaveStoreContext: function () {
      this.storeContext = null
      this.listGuard.invalidate()
      this.detailGuard.invalidate()

      this.tableData = []
      this.selectData = null
      this.checkedData = []

      this.closeDialogVisible = false
      this.dictionaryDialogFormVisible = false
      this.dialogFormVisible = false
      this.inputForm = this.initInputForm()
      this.formError = this.initFormError()
    },
    // 할인 대상 조회
    initDiscountTarget: function (storeCd) {
      this.mainVue.$axios.search(
        '/code/getCode/DISCOUNT_TARGET',
        null,
        function (result) {
          this.discountTarget = result
        }.bind(this),
        false
      )
    },
    // 판매 방식 데이타 초기화
    initSalesTp: function () {
      this.mainVue.$axios.search(
        '/code/getCode/SALES_TP',
        null,
        function (result) {
          this.salesTp = result
        }.bind(this),
        false
      )
    },
    // 할인 단말 구분 조회
    initDiscountClient: function (storeCd) {
      this.mainVue.$axios.search(
        '/code/getCode/DISCOUNT_CLIENT',
        null,
        function (result) {
          this.discountClient = result
        }.bind(this),
        false
      )
    },
    // 할인 구분 조회
    initDiscountTp: function (storeCd) {
      this.mainVue.$axios.search(
        '/code/getCode/DISCOUNT_TP',
        null,
        function (result) {
          this.discountTp = result
        }.bind(this),
        false
      )
    },
    // 추가
    add: function () {
      this.inputForm = this.initInputForm()
      this.inputForm.title = this.$t('할인 추가')

      this.formError = this.initFormError()

      if (this.storeLans.length > 0) {
        this.inputForm.discountInfos = []

        for (var i = 0, len = this.storeLans.length; i < len; i++) {
          this.formError['discountInfos[' + i + '].discountInfoNm'] = ''
          this.formError['discountInfos[' + i + '].price'] = ''
          this.formError['discountInfos[' + i + '].percente'] = ''

          this.inputForm.discountInfos.push({
            lanTp: this.storeLans[i],
            discountInfoNm: '',
            price: 0,
            percente: 0,
          })

          if (i === 0) {
            this.activiTab = this.storeLans[i].id
          }
        }
      }

      this.inputForm.discountTarget =
        this.discountTarget.length > 0 ? util.copyObj(this.discountTarget[0]) : { cd: null, nm: '' }
      this.inputForm.salesTps = this.salesTp.length > 0 ? util.copyObj(this.salesTp) : []
      this.inputForm.discountClients = this.discountClient.length > 0 ? util.copyObj(this.discountClient) : []
      this.inputForm.discountTp = this.discountTp.length > 0 ? util.copyObj(this.discountTp[0]) : { cd: null, nm: '' }

      // 순번 자동으로 검색된 내용중 제일 마지막으로 설정
      for (var i = 0, len = this.tableData.length; i < len; i++) {
        if (this.inputForm.ord < this.tableData[i].ord) {
          this.inputForm.ord = this.tableData[i].ord
        }
      }

      this.inputForm.ord++

      this.dialogFormVisible = true
    },
    // 수정
    up: function () {
      const seq = this.detailGuard.begin()

      this.mainVue.$axios.get(
        this.url + '/' + this.selectData.discountCd,
        function (result) {
          if (!this.detailGuard.isLatest(seq)) return

          result.statu = 'U'
          result.title = this.$t('할인 수정')

          this.formError = this.initFormError()

          // 발급 한도·게시 기간·랜덤팝업은 폼 표현으로 옮겨 담는다 — 서버의 무제한(null)을 그대로 두면
          // el-input-number 가 그것을 1 로 바꾼다(utils/discountOffer 의 discountOfferForm)
          this.inputForm = Object.assign({}, result, discountOfferForm(result))

          // default couponOnlyYn when missing in existing data
          if (typeof this.inputForm.couponOnlyYn === 'undefined' || this.inputForm.couponOnlyYn === null) {
            this.inputForm.couponOnlyYn = 'N'
          }
          if (!(this.inputForm.couponValidityDays > 0)) {
            this.inputForm.couponValidityDays = 30
          }
          if (this.inputForm.couponReissueYn !== 'Y') {
            this.inputForm.couponReissueYn = 'N'
          }

          // 수정 시 매장 다국어 삭제 시 카테고리도 다국어 지워줌
          var discountInfosTmp = []

          if (this.inputForm.statu == 'U') {
            discountInfosTmp = util.copyObj(this.inputForm.discountInfos)
          }

          this.inputForm.discountInfos = []

          for (var i = 0, len = this.storeLans.length; i < len; i++) {
            this.formError['discountInfos[' + i + '].discountInfoNm'] = ''
            this.formError['discountInfos[' + i + '].price'] = ''
            this.formError['discountInfos[' + i + '].percente'] = ''

            var discountInfoNm = ''
            var price = 0
            var percente = 0

            if (this.inputForm.statu == 'U') {
              for (var k = 0, klen = discountInfosTmp.length; k < klen; k++) {
                if (discountInfosTmp[k].lanTp.id == this.storeLans[i].id) {
                  discountInfoNm = discountInfosTmp[k].discountInfoNm
                  price = discountInfosTmp[k].price
                  percente = discountInfosTmp[k].percente
                }
              }
            }

            this.inputForm.discountInfos.push({
              lanTp: this.storeLans[i],
              discountInfoNm: discountInfoNm,
              price: price,
              percente: percente,
            })

            if (i === 0) {
              this.activiTab = this.storeLans[i].id
            }
          }

          // ★★ **새 언어 행은 0 으로 태어난다** (`I18N-ROW-SEED`) — 위 복사는 이전 행이
          // 있을 때만 값을 옮긴다. 기본값을 여기서 계산하지 않고 **동기화의 소유자**를
          // 부른다. 할인은 축이 둘이라 두 번 부르며, 그것이 곧 이 화면의 사실이다.
          // 타국화폐 결제 매장은 일부러 언어별 값을 따로 정하므로 건드리지 않는다.
          if (!this.internationalPayYn) {
            syncLangValue(this.inputForm.discountInfos, 'price', this.inputForm.price)
            syncLangValue(this.inputForm.discountInfos, 'percente', this.inputForm.percente)
          }

          this.dialogFormVisible = true
        }.bind(this),
        true
      )
    },
    // 조회
    search: function () {
      // 확정 컨텍스트가 없으면(진입 직후·전환 중) 읽을 매장이 없다 — Enter 키도 이 길로 온다
      if (!this.can.search) return

      const seq = this.listGuard.begin()

      this.mainVue.$axios.search(
        this.url,
        discountListQuery(this.source, this.searchForm, this.storeContext.storeCd),
        function (result) {
          // 그 사이 새 조회가 나갔거나 매장이 바뀌었다([leaveStoreContext]) — 이 목록은 주인이 없다
          if (!this.listGuard.isLatest(seq)) return

          for (var i = 0, len = result.length; i < len; i++) {
            if (result[i].discountTp.val == 'price') {
              result[i].tmp = this.mainVue.formatterNum(null, null, result[i].priceLan)
            } else {
              result[i].tmp = this.mainVue.formatterPercent(null, null, result[i].percenteLan)
            }
          }

          this.tableData = result
          // 표가 바뀌면 앞 표의 선택은 없다
          this.selectData = null
          this.checkedData = []

          this.inputForm = this.initInputForm()
          this.formError = this.initFormError()
        }.bind(this),
        false
      )
    },
    // 삭제
    del: function () {
      this.mainVue
        .$confirm(this.$t('할인 정보를 삭제 하시겠습니까?'), this.mainVue.appMsg.systemAlterTitle, {
          confirmButtonText: this.$t('삭제'),
          cancelButtonText: this.$t('취소'),
          type: 'info',
        })
        .then(
          function () {
            this.mainVue.$axios.del(
              this.url + '/' + this.checkedData.join(','),
              function (result) {
                this.mainVue.$message({ type: 'success', message: this.$t('삭제 성공') })
                this.search()
              }.bind(this),
              true
            )
          }.bind(this)
        )
        .catch(
          function () {
            this.mainVue.$message({ type: 'info', message: this.$t('삭제 취소') })
          }.bind(this)
        )
    },
    // 저장
    save: function () {
      if (!this.can.save) return

      this.formError = this.initFormError()

      for (var i = 0, len = this.inputForm.discountInfos.length; i < len; i++) {
        this.formError['discountInfos[' + i + '].discountInfoNm'] = ''
        this.formError['discountInfos[' + i + '].price'] = ''
        this.formError['discountInfos[' + i + '].percente'] = ''
      }

      // 서버 할인 저장은 전체 덮어쓰기다 — 발급 한도·게시 기간·랜덤팝업 다섯 칸을 언제나 싣는다.
      // 400(쿠폰 전용이 아닌 할인의 한도 등)은 인터셉터가 서버 문구 그대로 띄우고 창은 열린 채 남는다
      var payload = discountSavePayload(this.inputForm)

      if ('I' === this.inputForm.statu) {
        this.mainVue.$axios.add(
          this.url,
          discountAddBody(this.source, payload, this.storeContext.storeCd),
          this.formError,
          function (result) {
            this.mainVue.$message({ type: 'success', message: this.$t('등록 성공') })
            this.dialogFormVisible = false
            this.search()
          }.bind(this),
          true
        )
      } else {
        this.mainVue.$axios.up(
          this.url + '/' + this.inputForm.discountCd,
          payload,
          this.formError,
          function (result) {
            this.mainVue.$message({ type: 'success', message: this.$t('수정 성공') })
            this.dialogFormVisible = false
            this.search()
          }.bind(this),
          true
        )
      }
    },
    searchDictionary: function () {
      this.mainVue.$axios.search(
        '/dictionary/search',
        { nm: this.searchText, dictionaryTpVal: 'discount' },
        function (result) {
          this.popTableData = []

          for (var i = 0, len = result.length; i < len; i++) {
            var tmp = result[i]

            tmp[result[i].lan.id] = result[i].nm

            for (var j = 0, jlen = result[i].dictionarys.length; j < jlen; j++) {
              tmp[result[i].dictionarys[j].lan.id] = result[i].dictionarys[j].nm
            }

            this.popTableData.push(tmp)
          }
        }.bind(this),
        true
      )
    },
    // 키워드 데이타 선택
    selectDictionary: function (row) {
      for (var i = 0, len = this.lans.length; i < len; i++) {
        // 기본 언어 새팅
        if (0 == i) {
          this.inputForm.discountNm = row[this.lans[i].id]
        } else {
          // 타언어 순서대로 등록
          this.inputForm.discountInfos[i - 1].discountInfoNm = row[this.lans[i].id]
        }
      }

      this.dictionaryDialogFormVisible = false
    },
  },
}
</script>

<style scoped>
.offer-help {
  margin-left: 8px;
  color: #909399;
  cursor: help;
}

.offer-period-sep {
  margin: 0 8px;
}
</style>
